import React, { useRef, useEffect } from 'react';
import send from '../../images/chatbot/send.png';
import '../../ChatbotStyles/ChatInput.scss';

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  value: string;
  onChange: (value: string) => void;
  customerId: string;
  role: string;
  token: string;
  isLoading?: boolean;
  historyLoaded?: boolean;
}

export const ChatInput = ({
  onSendMessage,
  value,
  onChange,
  customerId,
  isLoading = false,
  historyLoaded = false,
}: ChatInputProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const validateInputs = (): boolean => {
    return !!(value.trim() && customerId.trim());
  };

  const isButtonDisabled = !validateInputs() || isLoading || !historyLoaded;

  const handleSubmit = (e: React.FormEvent) => {
    try {
      e.preventDefault();
      if (validateInputs() && !isLoading) {
        onSendMessage(value.trim());
        onChange('');
      }
    } catch (error) {
      console.error('Error submitting message:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    try {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        if (validateInputs() && !isLoading) {
          onSendMessage(value.trim());
          onChange('');
        }
      }
    } catch (error) {
      console.error('Error handling key press:', error);
    }
  };

  const adjustHeight = () => {
    try {
      if (textareaRef.current) {
        const textarea = textareaRef.current;

        textarea.style.height = 'auto';
        textarea.style.overflowY = 'hidden';

        const minHeight = 59;
        const maxHeight = 150;
        const scrollHeight = textarea.scrollHeight;
        const newHeight = Math.max(
          minHeight,
          Math.min(scrollHeight, maxHeight),
        );

        textarea.style.height = `${newHeight}px`;

        if (scrollHeight > maxHeight) {
          textarea.style.overflowY = 'auto';
        } else {
          textarea.style.overflowY = 'hidden';
        }
      }
    } catch (error) {
      console.error('Error adjusting textarea height:', error);
    }
  };

  useEffect(() => {
    adjustHeight();
  }, [value]);

  useEffect(() => {
    adjustHeight();
  }, []);

  return (
    <form onSubmit={handleSubmit} className="chat-input-form">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Type your message here..."
        rows={1}
      />
      <button
        type="submit"
        className="send-button"
        disabled={isButtonDisabled}
        aria-label="Send message"
        title={
          isLoading
            ? 'Please wait...'
            : !validateInputs()
              ? 'Please fill in all fields'
              : 'Send message'
        }
      >
        <img src={send} alt="sendicon" width={30} height={30} />
      </button>
    </form>
  );
};
